import { useContext, useEffect, useState } from "react";
import Toggle from "../Components/Input/Toggle";
import Modal from "../Components/Modal";
import Button from "../Components/Button";
import { DetailsContentHeader } from "./AdminPages/ProfilePages/NewAdmin";
import { contentPageContainerStyle, contentPageWrapperStyle } from "./AdminPages/ProfilePages/PartnerAdminList";
import { AuthContext, AuthContextType, FeatureFlagContext, FeatureFlagContextType, LangContext, LangContextType, LoadingContext, LoadingContextType, ToastContext, ToastContextType, useFullPortalEnabled } from "../utilities/customHooks";
import { getRegionSettings, updateRegionSettings, getSiteConfig, updateAnnouncement } from "../services/adminService";
import { getPortalMode, updatePortalMode } from "../services/admin/regionService";
import { useUpdateRegionSettings } from "../services/admin/regionMutations";
import type { AnnouncementSettings } from "../services/admin/siteConfigService";
import textData from "../textData.json";
import { getEffectiveCapabilities } from "../utilities/regionCapabilities";
import { parseEmailList } from "../utilities/helperFunctions";
import { isAdminLevelRole, isSuperAdmin as isSuperAdminRole } from "../utilities/regionVisibility";
import { DatePickerInput } from "@components/ui/date-picker-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/ui/select";
import * as T from "../utilities/frontendTypes";
import SowCapsEditor from "./components/SowCapsEditor";
import AdminEmailPreferences from "./components/AdminEmailPreferences";
import OrgEmailNotificationSettings from "./components/OrgEmailNotificationSettings";

type SettingsTopTab = 'organization' | 'personal';
type SettingsTab = 'org' | 'caps' | 'features' | 'emails' | 'announcements';

export default function Settings() {

  const { userRole, userRegion } = useContext(AuthContext) as AuthContextType;
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;
  const { langState } = useContext(LangContext) as LangContextType;

  const featureFlagCtx = useContext(FeatureFlagContext) as FeatureFlagContextType;

  const fullPortalEnabled = useFullPortalEnabled();
  const capabilities = getEffectiveCapabilities(userRegion, fullPortalEnabled);
  const isAdmin = isAdminLevelRole(userRole);
  const isOrgAdmin = isAdmin && capabilities.hasFullPortal;
  const isSuperAdmin = isSuperAdminRole(userRole);

  const [topTab, setTopTab] = useState<SettingsTopTab>('organization');
  const [activeTab, setActiveTab] = useState<SettingsTab>(isAdmin ? 'org' : 'features');

  // SoW cost caps (effective table for this region; null until loaded)
  const [sowCaps, setSowCaps] = useState<T.SowCapTable | null>(null);

  // Retainer invoice state
  const [retainerInvoiceEnabled, setRetainerInvoiceEnabled] = useState(true);
  const [isRetainerConfirmModalOpen, setIsRetainerConfirmModalOpen] = useState(false);
  const [pendingRetainerValue, setPendingRetainerValue] = useState<boolean | null>(null);

  // Application Intake — persisted state (from DB)
  const [intakeIsOpen, setIntakeIsOpen] = useState(true);
  const [intakeStartDate, setIntakeStartDate] = useState<Date | null>(null);
  const [intakeEndDate, setIntakeEndDate] = useState<Date | null>(null);
  const [intakeTitleEng, setIntakeTitleEng] = useState('Program Update');
  const [intakeTitleFr, setIntakeTitleFr] = useState('Mise à jour du programme');
  const [intakeMessageEng, setIntakeMessageEng] = useState('');
  const [intakeMessageFr, setIntakeMessageFr] = useState('');
  const [intakeLoaded, setIntakeLoaded] = useState(false);

  // Intake-closed submission allowlist (raw textarea text; parsed on save)
  const [allowlistText, setAllowlistText] = useState('');

  // Intake modal state
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false);
  const [pendingIntakeOpen, setPendingIntakeOpen] = useState(true);
  const [intakeToggleKey, setIntakeToggleKey] = useState(0); // force re-render on cancel
  const [modalStartDate, setModalStartDate] = useState<Date | null>(null);
  const [modalEndDate, setModalEndDate] = useState<Date | null>(null);
  const [modalTitleEng, setModalTitleEng] = useState('Program Update');
  const [modalTitleFr, setModalTitleFr] = useState('Mise à jour du programme');
  const [modalMessageEng, setModalMessageEng] = useState('');
  const [modalMessageFr, setModalMessageFr] = useState('');
  const [previewLang, setPreviewLang] = useState<'eng' | 'fr'>('eng');
  const [closedFieldsExpanded, setClosedFieldsExpanded] = useState(false);

  // Portal mode state (superAdmin only)
  const [ioPortalEnabled, setIoPortalEnabled] = useState(true);
  const [isPortalModeConfirmOpen, setIsPortalModeConfirmOpen] = useState(false);
  const [pendingPortalValue, setPendingPortalValue] = useState(true);

  // Announcement state (superAdmin only)
  const [announcementLoaded, setAnnouncementLoaded] = useState(false);
  const [annIsActive, setAnnIsActive] = useState(false);
  const [annStartDate, setAnnStartDate] = useState<Date | null>(null);
  const [annEndDate, setAnnEndDate] = useState<Date | null>(null);
  const [annTitleEng, setAnnTitleEng] = useState('');
  const [annTitleFr, setAnnTitleFr] = useState('');
  const [annMessageEng, setAnnMessageEng] = useState('');
  const [annMessageFr, setAnnMessageFr] = useState('');
  const [annStyle, setAnnStyle] = useState<'info' | 'warning' | 'success'>('info');
  const [annCtaTextEng, setAnnCtaTextEng] = useState('');
  const [annCtaTextFr, setAnnCtaTextFr] = useState('');
  const [annCtaLink, setAnnCtaLink] = useState('');
  const [annPreviewLang, setAnnPreviewLang] = useState<'eng' | 'fr'>('eng');

  useEffect(() => {
    if (isAdmin) {
      getRegionSettings()
        .then((res) => {
          if (res.success && res.data) {
            setRetainerInvoiceEnabled(res.data.retainerInvoiceEnabled);
            setEmbeddedSigningEnabled(res.data.embeddedSigningEnabled ?? true);
            setSowCaps((res.data as T.RegionSettings).sowCaps ?? null);
            const intake = (res.data as T.RegionSettings).applicationIntake;
            if (intake) {
              setIntakeIsOpen(intake.isOpen);
              setIntakeStartDate(intake.startDate ? new Date(intake.startDate) : null);
              setIntakeEndDate(intake.endDate ? new Date(intake.endDate) : null);
              setIntakeTitleEng(intake.closedTitle?.eng ?? 'Program Update');
              setIntakeTitleFr(intake.closedTitle?.fr ?? 'Mise à jour du programme');
              setIntakeMessageEng(intake.closedMessage?.eng ?? '');
              setIntakeMessageFr(intake.closedMessage?.fr ?? '');
            }
            setAllowlistText(((res.data as T.RegionSettings).intakeAllowlistEmails ?? []).join("\n"));
            setIntakeLoaded(true);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to load region settings:', err);
        });
    }
  }, [isOrgAdmin]);

  // The IO portal toggle must read IO's actual doc — getRegionSettings returns the
  // caller's own region, which made this toggle always render CT's value (bug #107)
  useEffect(() => {
    if (isSuperAdmin) {
      getPortalMode('investOttawa')
        .then((res) => {
          if (res.success && res.data) {
            setIoPortalEnabled(res.data.fullPortalEnabled);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to load IO portal mode:', err);
        });
    }
  }, [isSuperAdmin]);

  // Load announcement settings for superAdmin
  useEffect(() => {
    if (isSuperAdmin) {
      getSiteConfig()
        .then((res) => {
          if (res.success && res.data) {
            const ann = res.data.announcement;
            setAnnIsActive(ann.isActive);
            setAnnStartDate(ann.startDate ? new Date(ann.startDate) : null);
            setAnnEndDate(ann.endDate ? new Date(ann.endDate) : null);
            setAnnTitleEng(ann.title?.eng ?? '');
            setAnnTitleFr(ann.title?.fr ?? '');
            setAnnMessageEng(ann.message?.eng ?? '');
            setAnnMessageFr(ann.message?.fr ?? '');
            setAnnStyle(ann.style ?? 'info');
            setAnnCtaTextEng(ann.ctaText?.eng ?? '');
            setAnnCtaTextFr(ann.ctaText?.fr ?? '');
            setAnnCtaLink(ann.ctaLink ?? '');
            setAnnouncementLoaded(true);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to load site config:', err);
        });
    }
  }, [isSuperAdmin]);

  // --- Retainer invoice handlers ---

  const handleRetainerInvoiceToggle = (isChecked: boolean) => {
    setPendingRetainerValue(isChecked);
    setRetainerInvoiceEnabled(isChecked);
    setIsRetainerConfirmModalOpen(true);
  };

  const confirmRetainerToggle = async () => {
    if (pendingRetainerValue === null) return;
    setIsRetainerConfirmModalOpen(false);
    showLoading();
    try {
      const res = await updateRegionSettings({ retainerInvoiceEnabled: pendingRetainerValue });
      if (res.success) {
        showToast({
          toastType: "success",
          message: `${textData.Pages.Settings.SuccessToast[langState]}${(res as T.HTTPResponse & { affectedCount?: number }).affectedCount != null ? ` (${(res as T.HTTPResponse & { affectedCount?: number }).affectedCount} company settings updated)` : ''}`,
        });
      }
    } catch {
      setRetainerInvoiceEnabled(!pendingRetainerValue);
      showToast({ toastType: "failure", message: textData.Pages.Settings.FailToast[langState] });
    }
    setPendingRetainerValue(null);
    hideLoading();
  };

  const cancelRetainerToggle = () => {
    if (pendingRetainerValue !== null) {
      setRetainerInvoiceEnabled(!pendingRetainerValue);
    }
    setPendingRetainerValue(null);
    setIsRetainerConfirmModalOpen(false);
  };

  // --- Embedded signing toggle handler ---

  const [embeddedSigningEnabled, setEmbeddedSigningEnabled] = useState(true);

  const handleEmbeddedSigningToggle = async (isChecked: boolean) => {
    setEmbeddedSigningEnabled(isChecked);
    showLoading();
    try {
      const res = await updateRegionSettings({ embeddedSigningEnabled: isChecked });
      if (res.success) {
        await featureFlagCtx?.refreshFlags();
        showToast({ toastType: "success", message: `E-Signature signing ${isChecked ? 'enabled' : 'disabled'}` });
      }
    } catch {
      setEmbeddedSigningEnabled(!isChecked);
      showToast({ toastType: "failure", message: "Failed to update signing settings" });
    }
    hideLoading();
  };

  // --- SoW cost caps handler ---

  // Save through the shared mutation hook so the loading overlay, toast, and —
  // critically — the SP `['sowCaps']` cache invalidation (Rule 2, cross-role)
  // are handled centrally. A raw service call left the SP's cap warnings stale.
  const saveSowCapsMutation = useUpdateRegionSettings();

  const handleSaveSowCaps = (data: {
    productCapOverrides: Record<string, { soft: number | null; hard: number | null }>;
    hourlyRateSoftCap: number;
  }) =>
    // Return the awaitable promise so the editor's `await onSave()` resolves on real
    // completion (its "Saving…" state tracks the mutation). The hook already shows the
    // error toast + loading overlay, so swallow the rejection here.
    saveSowCapsMutation
      .mutateAsync(data, {
        onSuccess: async () => {
          const refreshed = await getRegionSettings();
          if (refreshed.success && refreshed.data) {
            setSowCaps((refreshed.data as T.RegionSettings).sowCaps ?? null);
          }
        },
      })
      .then(() => undefined)
      .catch(() => undefined);

  // --- Intake allowlist ---

  const saveAllowlistMutation = useUpdateRegionSettings();

  const handleSaveAllowlist = () => {
    const emails = parseEmailList(allowlistText);
    const invalid = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid.length > 0) {
      showToast({ toastType: "failure", message: `Invalid email(s): ${invalid.join(", ")}` });
      return;
    }
    // Sends ONLY the sibling allowlist key — never the applicationIntake subdoc.
    saveAllowlistMutation
      .mutateAsync({ intakeAllowlistEmails: emails })
      .then(() => setAllowlistText(emails.join("\n")))
      .catch(() => undefined); // hook shows the error toast + overlay
  };

  // --- Intake toggle handlers ---

  const handleIntakeToggle = (isChecked: boolean) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    setPendingIntakeOpen(isChecked);

    setPreviewLang('eng');

    if (isChecked) {
      // Opening: "Intake Opens" defaults to today, clear close date
      setModalStartDate(today);
      setModalEndDate(null);
      setModalTitleEng(intakeTitleEng);
      setModalTitleFr(intakeTitleFr);
      setModalMessageEng(intakeMessageEng);
      setModalMessageFr(intakeMessageFr);
    } else {
      // Closing: "Intake Closes" defaults to today, "Intake Opens" left empty
      setModalStartDate(null);
      setModalEndDate(today);
      setModalTitleEng(intakeTitleEng);
      setModalTitleFr(intakeTitleFr);
      setModalMessageEng(intakeMessageEng || '');
      setModalMessageFr(intakeMessageFr || '');
    }

    setClosedFieldsExpanded(false);
    setIsIntakeModalOpen(true);
  };

  const cancelIntakeModal = () => {
    setIsIntakeModalOpen(false);
    // Force toggle to re-render with the original state
    setIntakeToggleKey(k => k + 1);
  };

  const confirmIntakeToggle = async () => {
    // Validation
    if (!pendingIntakeOpen) {
      // Closing: titles + messages required
      if (!modalTitleEng.trim() || !modalTitleFr.trim() || !modalMessageEng.trim() || !modalMessageFr.trim()) {
        showToast({ toastType: "failure", message: "Both English and French titles and messages are required when closing applications." });
        return;
      }
    } else {
      // Opening with a close date: titles + messages required
      if (modalEndDate && (!modalTitleEng.trim() || !modalTitleFr.trim() || !modalMessageEng.trim() || !modalMessageFr.trim())) {
        showToast({ toastType: "failure", message: "Both titles and messages are required when setting a close date." });
        return;
      }
    }

    if (modalEndDate && modalStartDate && modalEndDate < modalStartDate) {
      showToast({ toastType: "failure", message: "Close date must be after open date." });
      return;
    }

    setIsIntakeModalOpen(false);
    showLoading();
    try {
      const res = await updateRegionSettings({
        applicationIntake: {
          isOpen: pendingIntakeOpen,
          startDate: modalStartDate?.toISOString() ?? null,
          endDate: modalEndDate?.toISOString() ?? null,
          closedTitle: {
            eng: modalTitleEng,
            fr: modalTitleFr,
          },
          closedMessage: {
            eng: modalMessageEng,
            fr: modalMessageFr,
          },
        },
      });
      if (res.success) {
        // Update local state to reflect saved values
        setIntakeIsOpen(pendingIntakeOpen);
        setIntakeStartDate(modalStartDate);
        setIntakeEndDate(modalEndDate);
        setIntakeTitleEng(modalTitleEng);
        setIntakeTitleFr(modalTitleFr);
        setIntakeMessageEng(modalMessageEng);
        setIntakeMessageFr(modalMessageFr);
        showToast({ toastType: "success", message: pendingIntakeOpen ? "Applications are now open." : "Applications are now closed." });
      }
    } catch {
      showToast({ toastType: "failure", message: "Failed to update intake settings." });
    }
    hideLoading();
  };

  // --- Feature toggle handler ---

  const handleFeatureToggle = async (key: string, isChecked: boolean) => {
    if (!featureFlagCtx) return;
    try {
      await featureFlagCtx.updateFlags({ [key]: isChecked });
      showToast({ toastType: "success", message: "Feature toggle updated" });
    } catch {
      showToast({ toastType: "failure", message: "Failed to update feature toggle" });
      await featureFlagCtx.refreshFlags();
    }
  };

  // --- Portal mode handlers (superAdmin only) ---
  const handlePortalModeToggle = (isChecked: boolean) => {
    setPendingPortalValue(isChecked);
    setIsPortalModeConfirmOpen(true);
  };

  const confirmPortalModeToggle = async () => {
    showLoading();
    try {
      await updatePortalMode('investOttawa', pendingPortalValue);
      setIoPortalEnabled(pendingPortalValue);
      await featureFlagCtx?.refreshFlags();
      showToast({ toastType: "success", message: `IO portal mode ${pendingPortalValue ? 'enabled' : 'disabled'}` });
    } catch {
      showToast({ toastType: "failure", message: "Failed to update portal mode" });
      setIoPortalEnabled(!pendingPortalValue);
    }
    setIsPortalModeConfirmOpen(false);
    hideLoading();
  };

  const cancelPortalModeToggle = () => {
    setIoPortalEnabled(!pendingPortalValue);
    setIsPortalModeConfirmOpen(false);
  };

  // --- Announcement save handler ---
  const handleAnnouncementSave = async () => {
    if (annIsActive && (!annTitleEng.trim() || !annTitleFr.trim() || !annMessageEng.trim() || !annMessageFr.trim())) {
      showToast({ toastType: "failure", message: "Both English and French titles and messages are required for an active announcement." });
      return;
    }
    showLoading();
    try {
      const payload: AnnouncementSettings = {
        isActive: annIsActive,
        startDate: annStartDate?.toISOString() ?? null,
        endDate: annEndDate?.toISOString() ?? null,
        title: { eng: annTitleEng, fr: annTitleFr },
        message: { eng: annMessageEng, fr: annMessageFr },
        style: annStyle,
        ctaText: (annCtaTextEng || annCtaTextFr) ? { eng: annCtaTextEng, fr: annCtaTextFr } : null,
        ctaLink: annCtaLink || null,
      };
      const res = await updateAnnouncement(payload);
      if (res.success) {
        showToast({ toastType: "success", message: "Announcement settings saved." });
      }
    } catch {
      showToast({ toastType: "failure", message: "Failed to save announcement settings." });
    }
    hideLoading();
  };

  // --- Tab config ---
  // Two levels: Organization | Personal on top; the org/region-scoped tabs
  // nest under Organization. Personal holds per-account settings (email
  // notification preferences). SoW Cost Caps is a full-portal surface —
  // application-only (MV mode) regions don't get the tab (server rejects the
  // write too).

  const tabs: { key: SettingsTab; label: string; visible: boolean }[] = [
    { key: 'org', label: textData.Pages.Settings.GeneralTab[langState], visible: isAdmin },
    { key: 'caps', label: 'SoW Cost Caps', visible: isOrgAdmin },
    { key: 'features', label: 'Feature Toggles', visible: isAdmin && !!featureFlagCtx?.isLoaded && Object.keys(featureFlagCtx?.registry || {}).length > 0 },
    { key: 'emails', label: textData.Pages.Settings.OrgEmailNotifications.TabLabel[langState], visible: isAdmin },
    { key: 'announcements', label: 'Announcements', visible: isSuperAdmin },
  ];

  const visibleTabs = tabs.filter(t => t.visible);

  const topTabs: { key: SettingsTopTab; label: string; visible: boolean }[] = [
    { key: 'organization', label: textData.Pages.Settings.OrganizationTab[langState], visible: visibleTabs.length > 0 },
    { key: 'personal', label: textData.Pages.Settings.PersonalTab[langState], visible: isAdmin },
  ];

  const visibleTopTabs = topTabs.filter(t => t.visible);
  const showOrgContent = topTab === 'organization';

  // Intake modal helpers
  const isClosing = !pendingIntakeOpen;
  const messagesRequired = isClosing || !!modalEndDate;
  const previewTitle = previewLang === 'fr' ? modalTitleFr : modalTitleEng;
  const previewMessage = previewLang === 'fr' ? modalMessageFr : modalMessageEng;

  return (
    <div className={contentPageContainerStyle}>
      <div className={contentPageWrapperStyle}>
        <DetailsContentHeader
          label={textData.Pages.Settings.Settings[langState]}
        />

        {/* Top-level tabs: Organization | Personal — segmented pills so the
            scope switch reads as a level above the underline sub-tabs */}
        {visibleTopTabs.length > 1 &&
          <div className="flex gap-1 mt-2 mb-5 justify-start self-baseline bg-text/5 border border-text/10 rounded-full p-1">
            {visibleTopTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setTopTab(tab.key)}
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors
                  ${topTab === tab.key
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text/60 hover:text-text hover:bg-text/5'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }

        {/* Organization sub-tabs */}
        {showOrgContent && visibleTabs.length > 1 &&
          <div className="flex gap-0 border-b border-text/20 mb-6 justify-start self-baseline">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors relative
                  ${activeTab === tab.key
                    ? 'text-primary border-b-2 border-primary -mb-[1px]'
                    : 'text-text/50 hover:text-text/70'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }

        {/* ---- Personal tab ---- */}
        {topTab === 'personal' && isAdmin &&
          <>
            {/* Single subtab for now — same style as the Organization sub-tabs
                so the two scopes read consistently */}
            <div className="flex gap-0 border-b border-text/20 mb-6 justify-start self-baseline">
              <button className="px-4 py-2.5 text-sm font-medium relative text-primary border-b-2 border-primary -mb-[1px]">
                {textData.Pages.Settings.EmailPreferences.EmailNotificationsHeading[langState]}
              </button>
            </div>
            <AdminEmailPreferences />
          </>
        }

        {/* ---- Organization Settings tab ---- */}
        {showOrgContent && activeTab === 'org' && isAdmin &&
          <div className="self-baseline w-full flex flex-col gap-8">

            {/* Retainer Invoice — only for regions with full portal */}
            {isOrgAdmin &&
              <div>
                <Toggle
                  key={String(retainerInvoiceEnabled)}
                  label={textData.Pages.Settings.RetainerInvoiceToggle[langState]}
                  isDefaultChecked={retainerInvoiceEnabled}
                  toggleActionCallback={handleRetainerInvoiceToggle}
                  checkedBg="bg-cSuccess"
                  uncheckedBg="bg-cMediumGrey"
                />
                <p className="text-sm text-text/50 mt-3 ml-1">
                  {textData.Pages.Settings.RetainerInvoiceDescription[langState]}
                </p>
              </div>
            }

            {/* E-Signature Signing — full portal regions only */}
            {isOrgAdmin &&
              <div>
                <div className="flex items-center gap-2">
                  <Toggle
                    key={`signing-${embeddedSigningEnabled}`}
                    label="E-Signature Signing"
                    isDefaultChecked={embeddedSigningEnabled}
                    toggleActionCallback={handleEmbeddedSigningToggle}
                    checkedBg="bg-cSuccess"
                    uncheckedBg="bg-cMediumGrey"
                  />
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                    Beta
                  </span>
                </div>
                <p className="text-sm text-text/50 mt-1 ml-1">
                  When enabled, admins can send SoWs for in-portal electronic signatures.
                  When disabled, admins use the manual &quot;Mark as Signed&quot; workflow.
                </p>
              </div>
            }

            {/* Portal Mode — superAdmin only, IO toggle */}
            {isSuperAdmin &&
              <div>
                <Toggle
                  key={`portal-${ioPortalEnabled}`}
                  label="Invest Ottawa: Full Portal Access"
                  isDefaultChecked={ioPortalEnabled}
                  toggleActionCallback={handlePortalModeToggle}
                  checkedBg="bg-cSuccess"
                  uncheckedBg="bg-cMediumGrey"
                />
                <p className="text-sm text-text/50 mt-1 ml-1">
                  When enabled, IO admins get the complete portal experience (SoWs, invoices, due diligence, etc.). When disabled, IO operates in application-only mode.
                </p>
              </div>
            }

            {/* Application Intake — all admins */}
            {intakeLoaded &&
              <div>
                <Toggle
                  key={`intake-${intakeIsOpen}-${intakeToggleKey}`}
                  label="Applications Open"
                  isDefaultChecked={intakeIsOpen}
                  toggleActionCallback={handleIntakeToggle}
                  checkedBg="bg-cSuccess"
                  uncheckedBg="bg-cMediumGrey"
                />
                <p className="text-sm text-text/50 mt-1 ml-1">
                  Controls whether founders can start new applications. Existing applications are unaffected.
                </p>
                {/* Summary of current state */}
                <div className="mt-3 ml-1 text-xs text-text/40 flex flex-col gap-0.5">
                  {intakeStartDate && <span>Opens: {intakeStartDate.toLocaleDateString()}</span>}
                  {intakeEndDate && <span>Closes: {intakeEndDate.toLocaleDateString()}</span>}
                  {(intakeMessageEng || intakeMessageFr) && <span>Closed message configured</span>}
                </div>

                {/* Intake-closed submission allowlist */}
                <div className="mt-6 flex flex-col gap-1">
                  <label className="text-sm font-medium text-text/70">Intake-Closed Submission Allowlist</label>
                  <p className="text-sm text-text/50">
                    Founders with these emails can submit a membership application even while intake is closed.
                    One email per line or comma-separated.
                  </p>
                  <textarea
                    value={allowlistText}
                    onChange={(e) => setAllowlistText(e.target.value)}
                    rows={4}
                    className="border border-border rounded px-3 py-2 text-sm w-full max-w-lg resize-y"
                    placeholder="name@example.com"
                  />
                  <div className="flex items-center gap-3 mb-4">
                    <Button
                      buttonText="Save Allowlist"
                      actionHandler={handleSaveAllowlist}
                      buttonType="secondary"
                    />
                    <span className="text-xs text-text/40">
                      {parseEmailList(allowlistText).length} email(s)
                    </span>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        {/* ---- SoW Cost Caps tab (full-portal regions only) ---- */}
        {showOrgContent && activeTab === 'caps' && isOrgAdmin &&
          <div className="self-baseline w-full">
            {sowCaps
              ? <SowCapsEditor initial={sowCaps} onSave={handleSaveSowCaps} />
              : <p className="text-sm text-text/50">Loading cost caps…</p>
            }
          </div>
        }

        {/* ---- Org Email Notifications tab ---- */}
        {/* Org-wide toggles (whole-region suppression) — distinct from the
            Personal → Email Notifications subtab, which is per-account */}
        {showOrgContent && activeTab === 'emails' && isAdmin &&
          <OrgEmailNotificationSettings hasFullPortal={capabilities.hasFullPortal} usesGrowthCoaches={capabilities.usesGrowthCoaches} />
        }

        {/* ---- Announcements tab (superAdmin only) ---- */}
        {showOrgContent && activeTab === 'announcements' && isSuperAdmin && announcementLoaded &&
          <div className="self-baseline w-full flex flex-col gap-6">
            <Toggle
              key={`ann-${annIsActive}`}
              label="Landing Page Announcement"
              isDefaultChecked={annIsActive}
              toggleActionCallback={(checked) => setAnnIsActive(checked)}
              checkedBg="bg-cSuccess"
              uncheckedBg="bg-cMediumGrey"
            />
            <p className="text-sm text-text/50 -mt-4 ml-1">
              When active, visitors to the landing page will see a banner announcement.
            </p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-text/70">Style</label>
              <Select value={annStyle} onValueChange={(value) => setAnnStyle(value as 'info' | 'warning' | 'success')}>
                <SelectTrigger className="text-sm w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info (Blue)</SelectItem>
                  <SelectItem value="warning">Warning (Yellow)</SelectItem>
                  <SelectItem value="success">Success (Green)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-6 flex-wrap">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-text/70">Start Date <span className="text-text/40">(optional)</span></label>
                <DatePickerInput
                  selected={annStartDate}
                  onChange={(date) => setAnnStartDate(date)}
                  isClearable
                  placeholderText="Optional"
                  dateFormat="yyyy-MM-dd"
                  className="border border-border rounded px-3 py-2 text-sm w-[200px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-text/70">End Date <span className="text-text/40">(optional)</span></label>
                <DatePickerInput
                  selected={annEndDate}
                  onChange={(date) => setAnnEndDate(date)}
                  isClearable
                  placeholderText="Optional"
                  dateFormat="yyyy-MM-dd"
                  minDate={annStartDate ?? undefined}
                  className="border border-border rounded px-3 py-2 text-sm w-[200px]"
                />
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <label className="text-sm font-medium text-text/70">Title (English) {annIsActive && <span className="text-cError">*</span>}</label>
                <input
                  type="text"
                  value={annTitleEng}
                  onChange={(e) => setAnnTitleEng(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm w-full"
                  placeholder="Program Update"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <label className="text-sm font-medium text-text/70">Title (French) {annIsActive && <span className="text-cError">*</span>}</label>
                <input
                  type="text"
                  value={annTitleFr}
                  onChange={(e) => setAnnTitleFr(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm w-full"
                  placeholder="Mise à jour du programme"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-text/70">Message (English) {annIsActive && <span className="text-cError">*</span>}</label>
              <textarea
                value={annMessageEng}
                onChange={(e) => setAnnMessageEng(e.target.value)}
                rows={3}
                className="border border-border rounded px-3 py-2 text-sm w-full resize-y"
                placeholder="Announcement message for landing page visitors..."
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-text/70">Message (French) {annIsActive && <span className="text-cError">*</span>}</label>
              <textarea
                value={annMessageFr}
                onChange={(e) => setAnnMessageFr(e.target.value)}
                rows={3}
                className="border border-border rounded px-3 py-2 text-sm w-full resize-y"
                placeholder="Message d'annonce pour les visiteurs de la page d'accueil..."
              />
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <label className="text-sm font-medium text-text/70">CTA Button Text (English) <span className="text-text/40">(optional)</span></label>
                <input
                  type="text"
                  value={annCtaTextEng}
                  onChange={(e) => setAnnCtaTextEng(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm w-full"
                  placeholder="Register Now"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <label className="text-sm font-medium text-text/70">CTA Button Text (French) <span className="text-text/40">(optional)</span></label>
                <input
                  type="text"
                  value={annCtaTextFr}
                  onChange={(e) => setAnnCtaTextFr(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm w-full"
                  placeholder="S'inscrire maintenant"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-text/70">CTA Link URL <span className="text-text/40">(optional)</span></label>
              <input
                type="text"
                value={annCtaLink}
                onChange={(e) => setAnnCtaLink(e.target.value)}
                className="border border-border rounded px-3 py-2 text-sm w-full"
                placeholder="/register"
              />
            </div>

            {/* Preview */}
            {(annTitleEng || annMessageEng) &&
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text/70">Preview</p>
                  <div className="flex text-xs gap-1">
                    <button
                      onClick={() => setAnnPreviewLang('eng')}
                      className={`px-2 py-0.5 rounded ${annPreviewLang === 'eng' ? 'bg-primary text-white' : 'text-text/50 hover:text-text/70'}`}
                    >ENG</button>
                    <button
                      onClick={() => setAnnPreviewLang('fr')}
                      className={`px-2 py-0.5 rounded ${annPreviewLang === 'fr' ? 'bg-primary text-white' : 'text-text/50 hover:text-text/70'}`}
                    >FR</button>
                  </div>
                </div>
                <div className={`border rounded-lg shadow-sm p-5 ${
                  annStyle === 'info' ? 'border-blue-200 bg-blue-50' :
                  annStyle === 'warning' ? 'border-cYellow bg-cLightYellow' :
                  'border-cSuccess/30 bg-cLightGreen'
                }`}>
                  <h3 className="text-lg font-bold mb-3">
                    {annPreviewLang === 'fr' ? annTitleFr : annTitleEng}
                  </h3>
                  <p className="text-sm whitespace-pre-wrap text-text/80">
                    {annPreviewLang === 'fr' ? annMessageFr : annMessageEng}
                  </p>
                  {(annCtaTextEng || annCtaTextFr) && annCtaLink &&
                    <div className="mt-3">
                      <Button
                        buttonText={annPreviewLang === 'fr' ? annCtaTextFr : annCtaTextEng}
                        buttonType="primary"
                        actionHandler={() => {}}
                      />
                    </div>
                  }
                </div>
              </div>
            }

            <div className="flex justify-end pt-2 mb-6">
              <Button
                buttonText="Save Announcement"
                actionHandler={handleAnnouncementSave}
              />
            </div>
          </div>
        }

        {/* ---- Feature Toggles tab ---- */}
        {showOrgContent && activeTab === 'features' && isAdmin && featureFlagCtx?.isLoaded &&
          <div className="self-baseline w-full">
            <div className="flex flex-col gap-5">
              {Object.entries(featureFlagCtx.registry).map(([key, meta]) => (
                <div key={key}>
                  <div className="flex items-center gap-2">
                    <Toggle
                      key={`${key}-${featureFlagCtx.flags[key]}`}
                      label={meta.label}
                      isDefaultChecked={featureFlagCtx.flags[key] ?? meta.defaultValue}
                      toggleActionCallback={(isChecked) => handleFeatureToggle(key, isChecked)}
                      checkedBg="bg-cSuccess"
                      uncheckedBg="bg-cMediumGrey"
                    />
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                      Beta
                    </span>
                  </div>
                  <p className="text-sm text-text/50 mt-1 ml-1">
                    {meta.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        }
      </div>

      {/* Retainer Invoice confirmation modal */}
      <Modal
        isOpen={isRetainerConfirmModalOpen}
        closeModal={cancelRetainerToggle}
        modalHeader="Override Retainer Invoice Setting"
        contentLabel="Override Retainer Invoice Setting"
        modalHTML={
          <div className="flex flex-col p-4 gap-6 w-full">
            <p className="bodyLarger font-semibold text-cWarning">Warning</p>
            <p className="bodyLarger">
              This will override the retainer invoice setting for <strong>all companies at SoW Signed stage or earlier</strong> in your organization. This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end pt-2">
              <Button
                buttonType="secondary"
                buttonText="Cancel"
                actionHandler={cancelRetainerToggle}
              />
              <Button
                buttonText="Confirm Override"
                actionHandler={confirmRetainerToggle}
              />
            </div>
          </div>
        }
      />

      {/* Portal Mode confirmation modal */}
      <Modal
        isOpen={isPortalModeConfirmOpen}
        closeModal={cancelPortalModeToggle}
        modalHeader="Change IO Portal Mode"
        contentLabel="Change IO Portal Mode"
        modalHTML={
          <div className="flex flex-col p-4 gap-6 w-full">
            <p className="bodyLarger font-semibold text-cWarning">Warning</p>
            <p className="bodyLarger">
              {pendingPortalValue
                ? "This will give IO admins full portal access including SoWs, invoices, due diligence, and all role invitations."
                : "This will restrict IO admins to application-only mode. They will lose access to invoices, SoWs, due diligence, and will only be able to invite Admins and Service Providers."
              }
            </p>
            <div className="flex gap-4 justify-end pt-2">
              <Button
                buttonType="secondary"
                buttonText="Cancel"
                actionHandler={cancelPortalModeToggle}
              />
              <Button
                buttonText="Confirm"
                actionHandler={confirmPortalModeToggle}
              />
            </div>
          </div>
        }
      />

      {/* Application Intake confirmation modal */}
      <Modal
        isOpen={isIntakeModalOpen}
        closeModal={cancelIntakeModal}
        modalHeader={isClosing ? "Close Applications" : "Open Applications"}
        contentLabel="Application Intake Settings"
        modalHTML={
          <div className="flex flex-col p-4 gap-5 w-full min-w-[400px]">
            <p className="text-sm text-text/70">
              {isClosing
                ? "Applications will be closed. Founders will see the message below on their dashboard."
                : "Applications will be opened. Founders will be able to start new applications."
              }
            </p>

            <div className="flex gap-6 flex-wrap">
              <div className="flex flex-col gap-1">
                <label htmlFor="modal-start-date" className="text-sm font-medium text-text/70">
                  Intake Opens {!isClosing && <span className="text-text/40">(required)</span>}
                </label>
                <DatePickerInput
                  id="modal-start-date"
                  selected={modalStartDate}
                  onChange={(date) => setModalStartDate(date)}
                  isClearable={isClosing}
                  placeholderText={isClosing ? "Optional" : "Select date"}
                  dateFormat="yyyy-MM-dd"
                  className="border border-border rounded px-3 py-2 text-sm w-[200px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="modal-end-date" className="text-sm font-medium text-text/70">
                  Intake Closes {isClosing && <span className="text-text/40">(required)</span>}
                </label>
                <DatePickerInput
                  id="modal-end-date"
                  selected={modalEndDate}
                  onChange={(date) => {
                    setModalEndDate(date);
                  }}
                  isClearable={!isClosing}
                  placeholderText={!isClosing ? "Optional" : "Select date"}
                  dateFormat="yyyy-MM-dd"
                  minDate={modalStartDate ?? undefined}
                  className="border border-border rounded px-3 py-2 text-sm w-[200px]"
                />
              </div>
            </div>

            {/* Closed intake message fields — collapsed by default when opening */}
            {(isClosing || closedFieldsExpanded) ? (
              <>
                {!isClosing && (
                  <p className="text-xs text-text/50 italic">
                    These fields configure the message founders see when the intake is closed.
                  </p>
                )}
                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                    <label htmlFor="modal-title-eng" className="text-sm font-medium text-text/70">
                      Title (English) {messagesRequired && <span className="text-cWarning">*</span>}
                    </label>
                    <input
                      id="modal-title-eng"
                      type="text"
                      value={modalTitleEng}
                      onChange={(e) => setModalTitleEng(e.target.value)}
                      className="border border-border rounded px-3 py-2 text-sm w-full"
                      placeholder="Program Update"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                    <label htmlFor="modal-title-fr" className="text-sm font-medium text-text/70">
                      Title (French) {messagesRequired && <span className="text-cWarning">*</span>}
                    </label>
                    <input
                      id="modal-title-fr"
                      type="text"
                      value={modalTitleFr}
                      onChange={(e) => setModalTitleFr(e.target.value)}
                      className="border border-border rounded px-3 py-2 text-sm w-full"
                      placeholder="Mise à jour du programme"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="modal-msg-eng" className="text-sm font-medium text-text/70">
                    Message (English) {messagesRequired && <span className="text-cWarning">*</span>}
                  </label>
                  <textarea
                    id="modal-msg-eng"
                    value={modalMessageEng}
                    onChange={(e) => setModalMessageEng(e.target.value)}
                    rows={3}
                    className="border border-border rounded px-3 py-2 text-sm w-full resize-y"
                    placeholder="Message shown to founders when applications are closed..."
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="modal-msg-fr" className="text-sm font-medium text-text/70">
                    Message (French) {messagesRequired && <span className="text-cWarning">*</span>}
                  </label>
                  <textarea
                    id="modal-msg-fr"
                    value={modalMessageFr}
                    onChange={(e) => setModalMessageFr(e.target.value)}
                    rows={3}
                    className="border border-border rounded px-3 py-2 text-sm w-full resize-y"
                    placeholder="Message affiché aux fondateurs lorsque les candidatures sont fermées..."
                  />
                </div>
              </>
            ) : (
              <button
                onClick={() => setClosedFieldsExpanded(true)}
                className="text-sm text-cPrimaryGreen hover:text-cSecondaryGreen transition-colors text-left"
              >
                Configure closed intake message (optional) &rsaquo;
              </button>
            )}

            {/* Preview — rendered as founders will see it */}
            {(previewTitle || previewMessage) &&
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text/70">Preview</p>
                  <div className="flex text-xs gap-1">
                    <button
                      onClick={() => setPreviewLang('eng')}
                      className={`px-2 py-0.5 rounded ${previewLang === 'eng' ? 'bg-primary text-white' : 'text-text/50 hover:text-text/70'}`}
                    >ENG</button>
                    <button
                      onClick={() => setPreviewLang('fr')}
                      className={`px-2 py-0.5 rounded ${previewLang === 'fr' ? 'bg-primary text-white' : 'text-text/50 hover:text-text/70'}`}
                    >FR</button>
                  </div>
                </div>
                <div className="border border-text/20 rounded-lg bg-white shadow-sm p-5">
                  {previewTitle && <h3 className="text-lg font-bold mb-3">{previewTitle}</h3>}
                  {previewMessage && <p className="text-sm whitespace-pre-wrap text-text/80">{previewMessage}</p>}
                </div>
              </div>
            }

            <div className="flex gap-4 justify-end pt-2">
              <Button
                buttonType="secondary"
                buttonText="Cancel"
                actionHandler={cancelIntakeModal}
              />
              <Button
                buttonText={isClosing ? "Close Applications" : "Open Applications"}
                actionHandler={confirmIntakeToggle}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
